/**
 * tm/components/ContactFields.tsx — 상담 결과 「고객 정보」 칸 한 벌 (대표자 · 실제 대표 · 담당자 여럿)
 *  주 회사 · 함께 상담한 회사(관계사)가 같은 칸을 쓴다 (2026-10-18 지도사 「관계사 고객 정보도 따로 나와야지」)
 *  칸 4열(이름 · 휴대폰 · 이메일 · 빼기) — 대표자 · 실제 대표 · 담당자 줄의 칸 위치를 맞춘다(오와 열)
 */
import { Fragment } from 'react';
import type { CSSProperties } from 'react';
import type { Company, CompanyManager } from '../types';
import { extraManagersOf } from '../types';
import { C, T } from './ui';
import { typingPhone } from '../../leads/calc';   // 전화번호 하이픈 자동(2026-10-19)

export interface ContactValue {
  ceo: string; ceo_phone: string; ceo_email: string;
  manager: string; manager_phone: string; manager_email: string;
  /** 담당자 2, 3 … (companies.extra_managers) */
  mgrs: CompanyManager[];
  /** 실제 대표(명의 대표와 다를 때 · companies.real_ceo) */
  real: { on: boolean; name: string; phone: string };
}

/** 업체 행 → 고객 정보 칸 */
export function contactFromCompany(c: {
  ceo?: string | null; ceo_phone?: string | null; ceo_email?: string | null;
  manager?: string | null; manager_phone?: string | null; manager_email?: string | null;
  real_ceo?: string | null; real_ceo_phone?: string | null; extra_managers?: CompanyManager[] | string | null;
}): ContactValue {
  return {
    ceo: c.ceo || '', ceo_phone: c.ceo_phone || '', ceo_email: c.ceo_email || '',
    manager: c.manager || '', manager_phone: c.manager_phone || '', manager_email: c.manager_email || '',
    mgrs: extraManagersOf(c),
    real: { on: !!(c.real_ceo || c.real_ceo_phone), name: c.real_ceo || '', phone: c.real_ceo_phone || '' },
  };
}
/** 고객 정보 칸 → 업체 저장 값 */
export function contactPayload(v: ContactValue): Partial<Company> {
  return {
    ceo: v.ceo, ceo_phone: v.ceo_phone, ceo_email: v.ceo_email,
    manager: v.manager, manager_phone: v.manager_phone, manager_email: v.manager_email,
    extra_managers: v.mgrs.filter(m => m.name.trim() || m.phone.trim() || m.email.trim()),
    real_ceo: v.real.on ? v.real.name.trim() : '', real_ceo_phone: v.real.on ? v.real.phone.trim() : '',
  };
}

const inp: CSSProperties = { padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', minWidth: 0 };
const grid = (mb: number): CSSProperties => ({ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 28px', gap: 6, marginBottom: mb });
const lbl: CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.muted, marginBottom: 6 };
const x: CSSProperties = { border: 'none', background: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 };
const add: CSSProperties = { height: 30, padding: '0 12px', borderRadius: 15, border: `1px dashed ${C.accent}`, background: C.white, color: C.accent, fontSize: T.xs, fontWeight: 700, cursor: 'pointer' };

export default function ContactFields({ value: v, onChange }: { value: ContactValue; onChange: (next: ContactValue) => void }) {
  const set = (patch: Partial<ContactValue>) => onChange({ ...v, ...patch });
  const setMgr = (i: number, k: keyof CompanyManager, val: string) => set({ mgrs: v.mgrs.map((m, j) => (j === i ? { ...m, [k]: val } : m)) });
  return (
    <div>
      <div style={lbl}>대표자</div>
      <div style={grid(10)}>
        <input value={v.ceo} onChange={e => set({ ceo: e.target.value })} placeholder="대표자명" style={inp} />
        <input value={v.ceo_phone} onChange={e => set({ ceo_phone: typingPhone(e.target.value) })} placeholder="휴대폰번호" style={inp} />
        <input value={v.ceo_email} onChange={e => set({ ceo_email: e.target.value })} placeholder="이메일" style={inp} />
        <span />
      </div>
      {v.real.on ? (
        <>
          <div style={lbl}>실제 대표 <span style={{ fontWeight: 400 }}>명의 대표와 다를 때</span></div>
          <div style={grid(10)}>
            <input value={v.real.name} onChange={e => set({ real: { ...v.real, name: e.target.value } })} placeholder="실제 대표 이름" style={inp} />
            <input value={v.real.phone} onChange={e => set({ real: { ...v.real, phone: typingPhone(e.target.value) } })} placeholder="휴대폰번호" style={inp} />
            <span />
            <button type="button" aria-label="실제 대표 빼기" title="실제 대표 빼기" onClick={() => set({ real: { on: false, name: '', phone: '' } })} style={x}>×</button>
          </div>
        </>
      ) : (
        <button type="button" onClick={() => set({ real: { ...v.real, on: true } })} style={{ ...add, marginBottom: 10 }}>+ 실제 대표가 따로 있어요</button>
      )}
      <div style={lbl}>담당자</div>
      <div style={grid(0)}>
        <input value={v.manager} onChange={e => set({ manager: e.target.value })} placeholder="담당자명" style={inp} />
        <input value={v.manager_phone} onChange={e => set({ manager_phone: typingPhone(e.target.value) })} placeholder="휴대폰번호" style={inp} />
        <input value={v.manager_email} onChange={e => set({ manager_email: e.target.value })} placeholder="이메일" style={inp} />
        <span />
        {v.mgrs.map((m, i) => (
          <Fragment key={i}>
            <input value={m.name} onChange={e => setMgr(i, 'name', e.target.value)} placeholder="담당자명" style={inp} />
            <input value={m.phone} onChange={e => setMgr(i, 'phone', typingPhone(e.target.value))} placeholder="휴대폰번호" style={inp} />
            <input value={m.email} onChange={e => setMgr(i, 'email', e.target.value)} placeholder="이메일" style={inp} />
            <button type="button" aria-label={`담당자 ${i + 2} 빼기`} title="이 담당자 빼기" onClick={() => set({ mgrs: v.mgrs.filter((_, j) => j !== i) })} style={x}>×</button>
          </Fragment>
        ))}
      </div>
      {v.mgrs.length < 20 && (
        <button type="button" onClick={() => set({ mgrs: [...v.mgrs, { name: '', phone: '', email: '' }] })} style={{ ...add, marginTop: 8 }}>+ 담당자 추가</button>
      )}
    </div>
  );
}
